import { Modal, ModalProps } from 'react-bootstrap';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ControlledModal } from '../../common/ControlledModal';
import { uploadFileSchema, UploadFileSchema } from '../Forms/schemas';
import { UploadFileForm } from '../Forms/UploadFileForm';
import { useFileExplorerContext } from '../../../hooks/useFileExplorerContext';
import { useFileExplorerMutation } from '../../../hooks/useFileExplorerMutation';
import { TreeFile, uploadFiles } from '../services';
import { getFileNameWithoutExtension } from '../../../utils/files';

const GENERIC_ERROR_MESSAGE = 'Ocurrió un error al subir el archivo. Por favor, intenta nuevamente.';

export function UploadFileModal({
  show, onHide, files,
}: ModalProps & { files?: File[] }) {
  const { activeFolder, addNewlyAddedItems } = useFileExplorerContext();
  if (!activeFolder) return null;
  const mutationFn = (data: UploadFileSchema) => uploadFiles(data);
  const form = useForm<UploadFileSchema>({
    resolver: zodResolver(uploadFileSchema),
    values: {
      title: '',
      description: '',
      files: files ?? [],
      category: activeFolder.id,
    },
  });

  const handleSuccess = () => {
    form.reset();
    onHide?.();
  };

  const {
    mutateAsync, isLoading, reset,
  } = useFileExplorerMutation({
    mutationFn,
    onSuccess: handleSuccess,
  });

  const handleUploadWithToast = (data: UploadFileSchema) => {
    const uploadPromise = mutateAsync(data, {
      onSuccess(response) {
        const newFiles = response as TreeFile[];
        addNewlyAddedItems(newFiles.map((file) => file.id));
      },
    });
    const isSingleFile = data.files.length === 1;
    toast.promise(uploadPromise, {
      loading: `Estamos subiendo tus archivo${isSingleFile ? '' : 's'}...`,
      success: () => {
        const fileCount = data.files.length;
        return isSingleFile
          ? 'Archivo subido exitosamente'
          : `${fileCount} archivos subidos exitosamente`;
      },
      error: {
        message: 'Error subiendo archivo',
        description: GENERIC_ERROR_MESSAGE,
      },
    });
  };
  const handleClose = () => {
    form.reset();
    reset();
    onHide?.();
  };

  const formFiles = form.watch('files');

  useEffect(() => {
    if (formFiles.length === 1) {
      const { name } = formFiles[0];
      const nameWithoutExtension = getFileNameWithoutExtension(name);
      form.setValue('title', nameWithoutExtension);
    }
  }, [formFiles]);

  return (
    <ControlledModal
      centered
      show={show}
      onHide={handleClose}
      title="Subir archivo"
      description={`Estás creando un nuevo archivo en ${activeFolder?.name || 'la raíz'}.`}
      closeButton
    >
      <Modal.Body>
        <UploadFileForm form={form} onSubmit={handleUploadWithToast}>
          <ControlledModal.Actions handleClose={handleClose} cancelButton isPending={isLoading} />
        </UploadFileForm>
      </Modal.Body>
    </ControlledModal>
  );
}
